import React from 'react';
import { useIntl } from 'react-intl';
import { API, GQL } from '@gin-betting/core';
import { map } from 'lodash';

import features from 'services/feature-manager';
import { ALL } from '../../../constants';
import {
    getSelectedCountryKeyInfo,
    sortByTopTournaments,
} from '../../../utils';
import messages from '../../Categorizer/messages.js'; // TODO: probably need to move this in root translations
import ActiveRegionItem from './ActiveRegionItem';
import CountryRegionItem from './CountryRegionItem';
import { SportSummaryRow } from './SportSummaryRow';
import TournamentItem from './TournamentItem';
import TournamentItemWrapper from './TournamentItemWrapper';
import TournamentTitle from './TournamentTitle';
import useTournamentList from './useTournamentList';

interface Props {
    items: GQL.CategorizerItem[];
    withSportSummary?: boolean;
    isOrganization?: boolean;
}

const TournamentList: React.FC<Props> = ({
    items,
    withSportSummary = true,
    isOrganization,
}) => {
    const { formatMessage } = useIntl();
    const {
        isClassic,
        tournamentsWithLocation,
        tournamentsWithoutLocation,
        activeMobileCountryKey,
        closeOverlay,
        makeHandleClickAll,
    } = useTournamentList();
    const isActiveTopTournaments = features.isActive(
        features.list.CategorizerTopTournaments
    );

    const renderTournaments = (
        categorizerItems: API.CategorizerItem[],
        options: { withFlag: boolean } = { withFlag: true }
    ) =>
        map(categorizerItems, item => (
            <TournamentItemWrapper
                key={item.value}
                tournamentId={item.value}
                onClick={closeOverlay}
            >
                <TournamentItem
                    count={item.count}
                    title={
                        <TournamentTitle
                            item={item}
                            withFlag={options.withFlag}
                        />
                    }
                />
            </TournamentItemWrapper>
        ));

    const renderContent = (
        tournamentItems: API.CategorizerItem[],
        groupItems: JSX.Element[] | null = null
    ) => {
        const ids = tournamentItems.map(i => i.value);

        return (
            <React.Fragment>
                <TournamentItemWrapper onClick={makeHandleClickAll(ids)}>
                    <TournamentItem title={formatMessage(messages.selectAll)} />
                </TournamentItemWrapper>
                {withSportSummary && <SportSummaryRow />}
                {groupItems}

                {renderTournaments(
                    isActiveTopTournaments
                        ? sortByTopTournaments(tournamentItems)
                        : tournamentItems
                )}
            </React.Fragment>
        );
    };

    if ((!isClassic && activeMobileCountryKey === ALL) || isOrganization) {
        return renderContent(items);
    }

    const { selectedCountryKey } = getSelectedCountryKeyInfo(
        activeMobileCountryKey
    );

    const region = tournamentsWithLocation.find(
        ({ countryCode }) => countryCode === selectedCountryKey
    );

    if (selectedCountryKey && region) {
        return (
            <ActiveRegionItem
                key={region.countryCode}
                tournaments={region.items}
                countryCode={region.countryCode}
                countryName={region.countryName}
            >
                {renderTournaments(
                    isActiveTopTournaments
                        ? sortByTopTournaments(region.items)
                        : region.items,
                    {
                        withFlag: false,
                    }
                )}
            </ActiveRegionItem>
        );
    }

    return renderContent(
        tournamentsWithoutLocation,
        map(
            tournamentsWithLocation,
            ({ items: tournaments, countryCode, countryName }) => (
                <CountryRegionItem
                    key={countryCode}
                    tournaments={tournaments}
                    countryCode={countryCode}
                    countryName={countryName}
                />
            )
        )
    );
};

export default TournamentList;
